// src/pages/superadmin/Exclusions.jsx
// T3 - admin-editable manual exclusion list (by company number).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import NotificationBell from "../../components/NotificationBell";
import { useToastStack } from "../../arc/components/toast-stack/toast-stack";
import { Input } from "../../arc/components/input/input";
import { Button } from "../../arc/components/button/button";

const styles = `
  * { box-sizing: border-box; }
  .excl-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); }
  .excl-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07); position: sticky; top: 0; z-index: 20; }
  .excl-header-inner { max-width: 1100px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .excl-brand { display: flex; align-items: center; gap: 12px; color: #fff; font-weight: 600; }
  .excl-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.07); color: #fff; text-decoration: none; }
  .excl-main { max-width: 1100px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }
  .excl-panel { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .excl-panel-head { padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--surface-raised); }
  .excl-panel-title { font-size: 14px; font-weight: 600; }
  .excl-panel-sub { margin-top: 4px; font-size: 12px; color: var(--text-muted); }
  .excl-form { display: grid; grid-template-columns: 1fr 2fr auto; gap: 10px; padding: 16px 20px; }
  .excl-input { width: 100%; padding: 9px 12px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 13px; outline: none; }
  .excl-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }
  .excl-btn { display: inline-flex; align-items: center; justify-content: center; padding: 0 16px; min-height: 36px; border: 1px solid transparent; border-radius: 8px; background: var(--accent); color: #fff; font-size: 12.5px; font-weight: 600; cursor: pointer; }
  .excl-btn.secondary { background: transparent; border-color: var(--border); color: var(--text-muted); }
  .excl-btn.danger { background: transparent; border-color: var(--red-border); color: var(--red); }
  .excl-table { width: 100%; border-collapse: collapse; }
  .excl-table th { text-align: left; padding: 10px 16px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-faint); border-bottom: 1px solid var(--border); }
  .excl-table td { padding: 12px 16px; font-size: 13px; border-bottom: 1px solid var(--border); vertical-align: top; }
  .excl-table tr:last-child td { border-bottom: none; }
  .excl-num { font-family: var(--mono); font-size: 12px; color: var(--accent-mid); }
  .excl-name { font-weight: 600; }
  .excl-empty { padding: 40px 20px; text-align: center; color: var(--text-faint); font-size: 13px; }
  .excl-error { padding: 10px 12px; border: 1px solid var(--red-border); background: var(--red-dim); color: var(--red); border-radius: 8px; font-size: 12.5px; }
  @media (max-width: 720px) { .excl-form { grid-template-columns: 1fr; } }
`;

export default function Exclusions() {
  const { toast } = useToastStack();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyNumber, setCompanyNumber] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [autoRows, setAutoRows] = useState([]);
  const [autoTotal, setAutoTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiService.getCompanyExclusions();
      setRows(res.data || []);
    } catch (err) {
      setError("Failed to load exclusions");
    } finally {
      setLoading(false);
    }
  };

  const loadAutomatic = async () => {
    try {
      const res = await apiService.getExcludedCompanies({ limit: 50 });
      setAutoRows(res.data || []);
      setAutoTotal(res.pagination?.total || 0);
    } catch (err) {
      // keep manual list usable even if this read fails
    }
  };

  useEffect(() => {
    load();
    loadAutomatic();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await apiService.addCompanyExclusion({ companyNumber, reason });
      setCompanyNumber("");
      setReason("");
      toast?.({ title: "Company excluded", type: "success" });
      load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add exclusion");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row) => {
    try {
      await apiService.removeCompanyExclusion(row.id);
      toast?.({ title: "Exclusion removed", type: "success" });
      load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove exclusion");
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="excl-root">
        <header className="excl-header">
          <div className="excl-header-inner">
            <div className="excl-brand">
              <Link to="/super-admin" className="excl-back" title="Back">
                ←
              </Link>
              Manual exclusions
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <NotificationBell />
              <Link to="/super-admin/companies" style={{ color: "#fff", fontSize: 12.5 }}>
                Company pool
              </Link>
            </div>
          </div>
        </header>

        <main className="excl-main">
          <section className="excl-panel">
            <div className="excl-panel-head">
              <div className="excl-panel-title">Exclude a company</div>
              <div className="excl-panel-sub">
                Excluded companies still exist in the pool but leave the Eligible
                scope, so they never appear in Overview, priorities or forecasts.
                Automatic exclusions (status, type, SIC, name) run separately.
              </div>
            </div>
            <form className="excl-form" onSubmit={handleAdd}>
              <Input
                label="Company number"
                hideLabel
                className="excl-input"
                value={companyNumber}
                onChange={(e) => setCompanyNumber(e.target.value)}
                placeholder="Company number e.g. 12345678"
                required
              />
              <Input
                label="Reason"
                hideLabel
                className="excl-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (shown in audit/UI)"
                required
              />
              <Button type="submit" variant="primary" loading={saving}>
                Add exclusion
              </Button>
            </form>
            {error && (
              <div style={{ padding: "0 20px 16px" }}>
                <div className="excl-error">{error}</div>
              </div>
            )}
          </section>

          <section className="excl-panel">
            <div className="excl-panel-head">
              <div className="excl-panel-title">
                Manual exclusions ({rows.length})
              </div>
              <div className="excl-panel-sub">
                Every row is a recorded reason, by company number.
              </div>
            </div>
            {loading ? (
              <div className="excl-empty">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="excl-empty">No manual exclusions yet.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="excl-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Reason</th>
                      <th>Added</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="excl-num">{row.companyNumber}</div>
                          {row.companyName && (
                            <div className="excl-name">{row.companyName}</div>
                          )}
                        </td>
                        <td>{row.reason}</td>
                        <td style={{ color: "var(--text-faint)", whiteSpace: "nowrap" }}>
                          {new Date(row.createdAt).toLocaleDateString("en-GB")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Button
                            variant="danger"
                            size="sm"
                            type="button"
                            onClick={() => handleRemove(row)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="excl-panel">
            <div className="excl-panel-head">
              <div className="excl-panel-title">
                Automatically excluded ({autoTotal.toLocaleString()})
              </div>
              <div className="excl-panel-sub">
                Evaluated from company status, type, SIC and name before scoring.
                These are the reasons recorded for the first 50.
              </div>
            </div>
            {autoRows.length === 0 ? (
              <div className="excl-empty">No automatic exclusions found.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="excl-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Reason</th>
                      <th>Status / type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoRows.map((row) => (
                      <tr key={row.companyNumber}>
                        <td>
                          <div className="excl-num">{row.companyNumber}</div>
                          <div className="excl-name">{row.companyName}</div>
                        </td>
                        <td>{row.exclusionReason}</td>
                        <td style={{ color: "var(--text-muted)" }}>
                          {[row.companyStatus, row.companyType]
                            .filter(Boolean)
                            .join(" · ") || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}
